import isNil from 'lodash/isNil';
import axios, { AxiosRequestConfig, AxiosInstance, CancelTokenSource } from 'axios';
import {
  BaseAPIClient as ScaffoldBaseAPIClient,
  BaseAPIClientOptions,
  APIResponse,
  APIRequest,
} from '@groma-dev/scaffold';
import { AppConfig } from '../AppConfig';

export type { APIResponse, APIRequest };

export class BaseAPIClient extends ScaffoldBaseAPIClient {
  static prepareRequestHeaders(headers: any): any {
    const { apiVersion } = AppConfig.Settings.Server;

    return {
      'Content-Type': `vnd.groma.${apiVersion}+json`,
      Accept: `vnd.groma.${apiVersion}+json`,
      ...headers,
    };
  }

  static prepareRequestParams(params: any): any {
    return params;
  }

  static createClient = (
    options?: BaseAPIClientOptions,
    extraConfig?: AxiosRequestConfig
  ): { client: AxiosInstance; config: AxiosRequestConfig; cancelToken: CancelTokenSource } => {
    const result: any = {};
    const { baseUrl } = AppConfig.Settings.Server;

    const clientConfig: AxiosRequestConfig = {
      timeout: AppConfig.Settings.Server.timeout,
      baseURL: baseUrl,
      ...extraConfig,
    };

    result.client = axios.create(clientConfig);

    // Bubble up all 4XX status codes for UI to catch / handle.
    result.client.interceptors.response.use(undefined, (error: any) => {
      if (error.response && error.response.status < 500 && error.response.status >= 400) {
        return Promise.resolve(error.response);
      } else return Promise.reject(error);
    });

    if (
      !isNil(BaseAPIClient.interceptors.request.onFulfilled) ||
      !isNil(BaseAPIClient.interceptors.request.onRejected)
    ) {
      result.client.interceptors.request.use(
        BaseAPIClient.interceptors.request.onFulfilled,
        BaseAPIClient.interceptors.request.onRejected
      );
    }

    if (
      !isNil(BaseAPIClient.interceptors.response.onFulfilled) ||
      !isNil(BaseAPIClient.interceptors.response.onRejected)
    ) {
      result.client.interceptors.response.use(
        BaseAPIClient.interceptors.response.onFulfilled,
        BaseAPIClient.interceptors.response.onRejected
      );
    }

    const cancelTokenHelper = axios.CancelToken;
    result.cancelToken = cancelTokenHelper.source();

    const config: AxiosRequestConfig = {};

    config.headers = this.prepareRequestHeaders(clientConfig.headers ?? {});
    config.params = this.prepareRequestParams({});

    config.cancelToken = result.cancelToken.token;
    config.withCredentials = clientConfig.withCredentials ?? true;

    result.config = config;

    return result;
  };
}
